import { firstTextValue, getMediaUrl, getPhotoState } from './revealStateSelectors';

const PAGE_COVER_FIELDS = [
  'coverImage',
  'coverImageUrl',
  'coverUrl',
  'heroImage',
  'heroImageUrl',
  'heroPhoto',
  'heroPhotoUrl',
  'openingImage',
  'openingImageUrl',
  'giftBoxCoverUrl',
  'backgroundImageUrl',
];

const PHOTO_CANDIDATE_TYPES = new Set([
  'photo_story',
  'primary_media',
  'primaryMedia',
  'primary_media_legacy',
  'legacy_primary_media',
  'hero_media',
  'media_card',
  'frame_gallery',
  'then_now',
  'polaroid_booth',
  'tiny_museum',
  'shared_album',
]);

const DEFAULT_CROP = {
  x: 0,
  y: 0,
  zoom: 1,
  aspect: 9 / 16,
};

const normalizeCrop = (crop = {}) => ({
  x: Number.isFinite(Number(crop.x)) ? Number(crop.x) : DEFAULT_CROP.x,
  y: Number.isFinite(Number(crop.y)) ? Number(crop.y) : DEFAULT_CROP.y,
  zoom: Number.isFinite(Number(crop.zoom)) ? Number(crop.zoom) : DEFAULT_CROP.zoom,
  aspect: Number.isFinite(Number(crop.aspect)) ? Number(crop.aspect) : DEFAULT_CROP.aspect,
});

const makeCoverMedia = (url, source, crop = DEFAULT_CROP) => ({
  url: url || '',
  crop: normalizeCrop(crop),
  source,
});

const getOpeningCoverMedia = (pageData = {}) => {
  const openingCover = pageData.openingCover || {};
  const openingCoverUrl = firstTextValue(
    getMediaUrl(openingCover),
    openingCover.imageUrl,
    openingCover.url,
  );

  if (!openingCoverUrl) return null;
  return makeCoverMedia(openingCoverUrl, 'openingCover', openingCover.crop);
};

const getPageCoverUrl = (pageData = {}) => {
  const directCover = firstTextValue(
    ...PAGE_COVER_FIELDS.map((field) => pageData[field]),
    getMediaUrl(pageData.heroMedia),
    getMediaUrl(pageData.coverMedia),
    getMediaUrl(pageData.openingMedia),
  );
  if (directCover) return directCover;

  const heroMedia = pageData.heroMedia || {};
  return firstTextValue(
    getMediaUrl(heroMedia.image),
    getMediaUrl(heroMedia.photo),
    getMediaUrl(heroMedia.cover),
    getMediaUrl(heroMedia.thumbnail),
  );
};

const getPageCoverMedia = (pageData = {}) => {
  const pageCoverUrl = getPageCoverUrl(pageData);
  if (!pageCoverUrl) return null;
  return makeCoverMedia(pageCoverUrl, 'pageCover', pageData.openingCover?.crop);
};

const getFirstModulePhoto = (modules = [], preferredTypes = []) => {
  const preferredTypeSet = new Set(preferredTypes);
  const candidates = modules.filter((module) => preferredTypeSet.has(module?.type));

  for (const module of candidates) {
    const photoState = getPhotoState(module);
    const firstPhoto = photoState.cards?.find((card) => card?.url);
    if (firstPhoto?.url) return makeCoverMedia(firstPhoto.url, 'modulePhoto');
  }

  return null;
};

export const getLayeredGiftCoverMedia = ({
  pageData = {},
  modules = [],
  fallbackImage = '',
} = {}) => {
  const safeModules = Array.isArray(modules) ? modules : [];

  const candidates = [
    getOpeningCoverMedia(pageData),
    getPageCoverMedia(pageData),
    getFirstModulePhoto(safeModules, ['primary_media', 'primaryMedia', 'primary_media_legacy', 'legacy_primary_media', 'hero_media']),
    getFirstModulePhoto(safeModules, Array.from(PHOTO_CANDIDATE_TYPES)),
    makeCoverMedia(fallbackImage, 'themeFallback'),
  ];

  return candidates.find((candidate) => candidate?.url) || makeCoverMedia('', 'empty');
};

export const getLayeredGiftCoverImage = (options = {}) => getLayeredGiftCoverMedia(options).url;
